import { ModerationSettings } from '@tribeplatform/gql-client/types'
import { useAdminOnboarding } from '@tribeplatform/react-components/AdminOnboarding'
import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useUpdateModerationSettings } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { LoadingOverlay } from '@tribeplatform/react-ui-kit/LoadingOverlay'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

interface ModerationAlertsProps {
  moderationSettings: ModerationSettings
}

const reduceCustomBlackList = (str: string): Array<string> => {
  return (
    str
      .trim()
      // eslint-disable-next-line no-control-regex
      .split(new RegExp('[,\r\n]+'))
      .reduce((acc: string[], curr) => {
        const c = curr.trim()
        if (c !== '') {
          acc.push(c)
        }
        return acc
      }, [])
  )
}

export const ModerationAlerts = ({
  moderationSettings,
}: ModerationAlertsProps) => {
  const { $t } = useI18n()
  const { mutateAsync: updateModerationSettings, isLoading } =
    useUpdateModerationSettings()
  const { completeStep } = useAdminOnboarding()

  const {
    enableBlacklisting,
    customBlacklist,
    useDefaultBlacklisting,
    preventPublish,
  } = moderationSettings

  const onSubmit = data => {
    const {
      enableBlacklisting,
      useDefaultBlacklisting,
      customBlacklist,
      preventPublish,
    } = data

    const input = {
      enableBlacklisting,
      useDefaultBlacklisting,
      customBlacklist,
      preventPublish,
    }

    if (customBlacklist !== undefined) {
      input.customBlacklist = customBlacklist
        ? reduceCustomBlackList(customBlacklist)
        : []
    }

    updateModerationSettings({
      input,
    })
      .then(() => {
        completeStep({
          step: 'moderation-rules',
        })
        toast({
          title: $t({
            defaultMessage: 'Moderation settings updated.',
            id: 'Admin.ModerationSettings.Updated',
          }),
          status: 'success',
        })
      })
      .catch(() => {
        toast({
          title: $t({
            defaultMessage: 'Unable to update moderation settings.',
            id: 'Admin.ModerationSettings.FailedToUpdate',
          }),
          status: 'error',
        })
      })
  }

  return (
    <Form
      defaultValues={{
        enableBlacklisting,
        useDefaultBlacklisting,
        customBlacklist: customBlacklist?.join(','),
        preventPublish,
      }}
      onSubmit={onSubmit}
    >
      {({ formState: { isDirty }, watch }) => (
        <Card>
          <Card.Header
            title={$t({
              defaultMessage: 'Moderation alerts',
              id: 'Admin.ModerationSettings.ModerationAlerts',
            })}
            description={$t({
              defaultMessage:
                'Moderation alerts flag certain keywords or phrases in posts or comments for review before they are published. Admins are notified to review content and take action if necessary. Admin-created content bypasses moderation.',
              id: 'Admin.ModerationSettings.ModerationAlerts.Description',
            })}
          />
          <Card.Content>
            <div className="web:col-span-12 web:md:col-span-8 flex flex-col space-y-5">
              <Form.Toggle
                name="enableBlacklisting"
                label={$t({
                  defaultMessage: 'Enable keyword moderation',
                  id: 'Admin.ModerationSettings.EnableKeywordModeration',
                })}
              />
              <LoadingOverlay
                isLoading={watch('enableBlacklisting') === false}
                pulse={false}
              >
                <Form.Toggle
                  name="preventPublish"
                  label={$t({
                    defaultMessage: 'Publish after admin approval only',
                    id: 'Admin.ModerationSettings.PreventPublish',
                  })}
                />
              </LoadingOverlay>
              <LoadingOverlay
                isLoading={watch('enableBlacklisting') === false}
                pulse={false}
              >
                <Form.Toggle
                  name="useDefaultBlacklisting"
                  label={
                    <T
                      defaultMessage="<link>Include common profanities</link>"
                      id="Admin.ModerationSettings.DefaultList"
                      values={{
                        link: chunks => (
                          <Link
                            href="https://github.com/web-mech/badwords/blob/master/lib/lang.json"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {chunks}
                          </Link>
                        ),
                      }}
                    />
                  }
                />
              </LoadingOverlay>
              <LoadingOverlay
                isLoading={watch('enableBlacklisting') === false}
                pulse={false}
              >
                <Form.Textarea
                  name="customBlacklist"
                  label={$t({
                    defaultMessage: 'Keywords',
                    id: 'Admin.ModerationSettings.Keywords',
                  })}
                  helperText={$t({
                    defaultMessage:
                      'Enter keywords Separated by commas or new lines. (e.g. "badword1, badword2")',
                    id: 'Admin.ModerationSettings.Keywords.HelperText',
                  })}
                />
              </LoadingOverlay>
              <Form.Actions>
                <Button type="submit" loading={isLoading} disabled={!isDirty}>
                  <T defaultMessage="Update" id="Generics.Update" />
                </Button>
              </Form.Actions>
            </div>
          </Card.Content>
        </Card>
      )}
    </Form>
  )
}
